import { useState, useEffect, useCallback } from 'react'
import { adminActivities } from '../api/client'
import { styles as as } from './adminStyles'
import type { SynonymEntry } from '../games/synonyms/useSynonyms'

interface Props { onBack: () => void }

interface Draft {
  esWord:      string
  esSynonyms:  string   // comma-separated
  esAntonyms:  string
  enWord:      string
  enSynonyms:  string
  enAntonyms:  string
}

const BLANK: Draft = { esWord: '', esSynonyms: '', esAntonyms: '', enWord: '', enSynonyms: '', enAntonyms: '' }
const SLUG = 'synonyms'

function nextId(entries: SynonymEntry[]): number {
  return entries.length === 0 ? 1 : Math.max(...entries.map(e => e.id)) + 1
}

function draftFromEntry(e: SynonymEntry): Draft {
  return {
    esWord:     e.es.word,
    esSynonyms: e.es.synonyms.join(', '),
    esAntonyms: e.es.antonyms.join(', '),
    enWord:     e.en.word,
    enSynonyms: e.en.synonyms.join(', '),
    enAntonyms: e.en.antonyms.join(', '),
  }
}

function parseList(s: string): string[] {
  return s.split(',').map(x => x.trim()).filter(Boolean)
}

function draftToEntry(draft: Draft, id: number): SynonymEntry {
  return {
    id,
    es: { word: draft.esWord.trim(), synonyms: parseList(draft.esSynonyms), antonyms: parseList(draft.esAntonyms) },
    en: { word: draft.enWord.trim(), synonyms: parseList(draft.enSynonyms), antonyms: parseList(draft.enAntonyms) },
  }
}

function isValid(draft: Draft): boolean {
  return Boolean(
    draft.esWord.trim() &&
    parseList(draft.esSynonyms).length > 0 &&
    parseList(draft.esAntonyms).length > 0 &&
    draft.enWord.trim() &&
    parseList(draft.enSynonyms).length > 0 &&
    parseList(draft.enAntonyms).length > 0,
  )
}

export default function SynonymThemeEditor({ onBack }: Props) {
  const [entries,  setEntries]  = useState<SynonymEntry[]>([])
  const [loading,  setLoading]  = useState(true)
  const [saving,   setSaving]   = useState(false)
  const [error,    setError]    = useState<string | null>(null)
  const [savedOk,  setSavedOk]  = useState(false)
  const [editingId, setEditingId] = useState<number | 'new' | null>(null)
  const [draft,    setDraft]    = useState<Draft>(BLANK)
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null)
  const [search, setSearch] = useState('')

  const load = useCallback(() => {
    setLoading(true); setError(null)
    adminActivities.get(SLUG)
      .then(detail => {
        const raw = (detail.data as { entries?: SynonymEntry[] } | null)
        setEntries(raw?.entries ?? [])
      })
      .catch(() => setError('Error cargando entradas'))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => { load() }, [load])

  async function save(updated: SynonymEntry[]) {
    setSaving(true); setError(null); setSavedOk(false)
    try {
      await adminActivities.patch(SLUG, { data: { entries: updated } })
      setEntries(updated); setSavedOk(true)
      setTimeout(() => setSavedOk(false), 2000)
    } catch {
      setError('Error al guardar')
    } finally {
      setSaving(false)
    }
  }

  function startNew() {
    setDraft(BLANK); setEditingId('new')
  }

  function startEdit(entry: SynonymEntry) {
    setDraft(draftFromEntry(entry)); setEditingId(entry.id)
  }

  function cancelEdit() {
    setEditingId(null); setDraft(BLANK)
  }

  async function commitEdit() {
    if (!isValid(draft)) return
    let updated: SynonymEntry[]
    if (editingId === 'new') {
      updated = [...entries, draftToEntry(draft, nextId(entries))]
    } else {
      updated = entries.map(e => e.id === editingId ? draftToEntry(draft, e.id) : e)
    }
    await save(updated)
    cancelEdit()
  }

  async function deleteEntry(id: number) {
    const updated = entries.filter(e => e.id !== id)
    await save(updated)
    setConfirmDelete(null)
  }

  const filtered = search.trim()
    ? entries.filter(e =>
        e.es.word.toLowerCase().includes(search.toLowerCase()) ||
        e.en.word.toLowerCase().includes(search.toLowerCase()),
      )
    : entries

  if (loading) return <div style={as.page}><div style={as.header}><button style={as.backBtn} onClick={onBack}>←</button><h1 style={as.title}>Sinónimos y Antónimos</h1></div><p>Cargando…</p></div>

  return (
    <div style={as.page}>
      <div style={as.header}>
        <button style={as.backBtn} onClick={onBack}>←</button>
        <h1 style={as.title}>Sinónimos y Antónimos</h1>
        <span style={ls.count}>{entries.length} palabras</span>
      </div>

      {error   && <p style={as.errorBanner}>{error}</p>}
      {savedOk && <p style={as.successBanner}>✓ Guardado</p>}

      {/* Search + Add */}
      <div style={ls.toolbar}>
        <input
          style={{ ...as.input, flex: 1 }}
          placeholder="Buscar por palabra…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <button style={as.primaryBtn} onClick={startNew} disabled={saving}>+ Nueva entrada</button>
      </div>

      {/* Edit form */}
      {editingId !== null && (
        <div style={ls.form}>
          <h2 style={ls.formTitle}>{editingId === 'new' ? 'Nueva entrada' : 'Editar entrada'}</h2>
          <div style={ls.formGrid}>
            <div style={ls.formCol}>
              <h3 style={ls.langHeader}>🇪🇸 Español</h3>
              <label style={as.label}>Palabra</label>
              <input style={as.input} value={draft.esWord} onChange={e => setDraft(p => ({ ...p, esWord: e.target.value }))} placeholder="ej. grande" />
              <label style={as.label}>Sinónimos (separados por coma)</label>
              <input style={as.input} value={draft.esSynonyms} onChange={e => setDraft(p => ({ ...p, esSynonyms: e.target.value }))} placeholder="ej. enorme, inmenso, vasto" />
              <label style={as.label}>Antónimos (separados por coma)</label>
              <input style={as.input} value={draft.esAntonyms} onChange={e => setDraft(p => ({ ...p, esAntonyms: e.target.value }))} placeholder="ej. pequeño, diminuto" />
            </div>
            <div style={ls.formCol}>
              <h3 style={ls.langHeader}>🇬🇧 English</h3>
              <label style={as.label}>Word</label>
              <input style={as.input} value={draft.enWord} onChange={e => setDraft(p => ({ ...p, enWord: e.target.value }))} placeholder="e.g. big" />
              <label style={as.label}>Synonyms (comma-separated)</label>
              <input style={as.input} value={draft.enSynonyms} onChange={e => setDraft(p => ({ ...p, enSynonyms: e.target.value }))} placeholder="e.g. huge, large, immense" />
              <label style={as.label}>Antonyms (comma-separated)</label>
              <input style={as.input} value={draft.enAntonyms} onChange={e => setDraft(p => ({ ...p, enAntonyms: e.target.value }))} placeholder="e.g. small, tiny" />
            </div>
          </div>
          <div style={ls.formActions}>
            <button style={as.primaryBtn} onClick={commitEdit} disabled={saving || !isValid(draft)}>
              {saving ? 'Guardando…' : editingId === 'new' ? 'Añadir' : 'Guardar cambios'}
            </button>
            <button style={as.ghostBtn} onClick={cancelEdit} disabled={saving}>Cancelar</button>
          </div>
        </div>
      )}

      {/* Entry list */}
      <div style={ls.list}>
        {filtered.length === 0 && <p style={ls.empty}>{search ? 'Sin resultados' : 'Sin entradas'}</p>}
        {filtered.map(entry => (
          <div key={entry.id} style={ls.row}>
            <div style={ls.rowMain}>
              <div style={ls.rowWord}>
                <span style={ls.rowEs}>{entry.es.word}</span>
                <span style={ls.rowSep}>/</span>
                <span style={ls.rowEn}>{entry.en.word}</span>
              </div>
              <div style={ls.rowDetail}>
                <span style={ls.badge}>≈ {entry.es.synonyms.slice(0, 3).join(', ')}</span>
                <span style={ls.badgeAnt}>↔ {entry.es.antonyms.slice(0, 2).join(', ')}</span>
              </div>
            </div>
            <div style={ls.rowActions}>
              <button style={as.smallBtn} onClick={() => startEdit(entry)}>Editar</button>
              {confirmDelete === entry.id
                ? (
                  <>
                    <button style={as.dangerBtn} onClick={() => deleteEntry(entry.id)}>Confirmar</button>
                    <button style={as.ghostBtn}  onClick={() => setConfirmDelete(null)}>Cancelar</button>
                  </>
                ) : (
                  <button style={as.dangerBtn} onClick={() => setConfirmDelete(entry.id)}>Borrar</button>
                )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

const ls: Record<string, React.CSSProperties> = {
  count: { marginLeft: 'auto', fontSize: '0.875rem', color: '#666', alignSelf: 'center' },
  toolbar: { display: 'flex', gap: '0.75rem', alignItems: 'center', marginBottom: '1rem' },
  form: {
    background: '#F8FAF8',
    border: '1px solid #D4E8D4',
    borderRadius: '8px',
    padding: '1.25rem',
    marginBottom: '1.5rem',
  },
  formTitle: { margin: '0 0 1rem', fontSize: '1rem', fontWeight: 'bold', color: '#1B4332' },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' },
  formCol: { display: 'flex', flexDirection: 'column', gap: '0.25rem' },
  langHeader: { margin: '0 0 0.75rem', fontSize: '0.875rem', fontWeight: 'bold', color: '#444' },
  formActions: { display: 'flex', gap: '0.75rem', marginTop: '1rem' },
  list: { display: 'flex', flexDirection: 'column', gap: '0.5rem' },
  empty: { color: '#666', fontStyle: 'italic', textAlign: 'center', padding: '2rem 0' },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0.75rem 1rem',
    background: '#fff',
    border: '1px solid #E8EEE8',
    borderRadius: '6px',
    gap: '1rem',
  },
  rowMain: { display: 'flex', flexDirection: 'column', gap: '0.25rem', minWidth: 0, flex: 1 },
  rowWord: { display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' },
  rowEs: { fontSize: '1rem', fontWeight: 'bold', color: '#1B4332' },
  rowSep: { color: '#999' },
  rowEn: { fontSize: '1rem', color: '#555' },
  rowDetail: { display: 'flex', gap: '0.5rem', flexWrap: 'wrap' },
  badge: {
    fontSize: '0.75rem',
    background: '#E8F5E9',
    color: '#2D6A4F',
    padding: '0.1rem 0.4rem',
    borderRadius: '4px',
  },
  badgeAnt: {
    fontSize: '0.75rem',
    background: '#FFF3E0',
    color: '#E65100',
    padding: '0.1rem 0.4rem',
    borderRadius: '4px',
  },
  rowActions: { display: 'flex', gap: '0.5rem', flexShrink: 0 },
}
